import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import Svg, { G, Line, Path, Text as SvgText } from 'react-native-svg';
import { fmt } from '@restorant/core';
import type { Bucket } from '@restorant/core';
import { radius, useColors } from '../theme';
import { T } from './Text';

const PLOT_H = 132;
const AXIS_H = 20;
const AXIS_W = 46;
const BAR_MAX_W = 24;
const BAR_GAP = 2;

/**
 * Revenue over one period, drawn as a real plot on react-native-svg.
 *
 * It used to be stacked Views. That could size a bar but not draw an axis, so
 * there was no scale to read a bar against — ₹20,918 sat above a row of shapes
 * that could have meant anything — and every empty day was given a 2px block to
 * stand in for zero, which strung a dotted line across the whole chart and read
 * as a broken border rather than as nothing sold.
 *
 * SVG rather than a charting library: react-native-svg is already a dependency
 * of both apps (it is what the icons are drawn with), it is the thing the RN
 * chart libraries are themselves built on, and the ones that render on both
 * Expo and react-native-web without a native rebuild are the ones this would
 * have to be rewritten around later.
 */
export function BarChart({
  buckets,
  max,
  emptyLabel,
  periodLabel,
  ordersLabel,
}: {
  buckets: Bucket[];
  max: number;
  /** Shown in place of the plot when nothing sold in the whole period. */
  emptyLabel: string;
  /** Names the period while no single bar is selected, e.g. "Today". */
  periodLabel: string;
  /** Formats an order count, e.g. (3) => "3 orders". */
  ordersLabel: (n: number) => string;
}) {
  const colors = useColors();
  const [picked, setPicked] = useState<number | null>(null);
  const [width, setWidth] = useState(0);

  const hasData = buckets.some((b) => b.revenue > 0);
  const selected = picked != null ? buckets[picked] : null;

  // With nothing selected the header reports the period as a whole, so the
  // space is never blank and never lies about what is being shown.
  const headline = selected
    ? {
        value: fmt(selected.revenue),
        detail: `${bucketLabel(selected)} · ${ordersLabel(selected.orders)}`,
      }
    : {
        value: fmt(buckets.reduce((s, b) => s + b.revenue, 0)),
        detail: `${periodLabel} · ${ordersLabel(buckets.reduce((s, b) => s + b.orders, 0))}`,
      };

  const onLayout = (e: LayoutChangeEvent) => setWidth(e.nativeEvent.layout.width);

  // The axis is drawn to a rounded ceiling, not to the tallest bar: ticks a
  // reader can do arithmetic with are the entire reason the axis is there.
  const top = niceCeiling(max);
  const plotW = Math.max(0, width - AXIS_W);
  const band = buckets.length ? plotW / buckets.length : 0;
  const barW = Math.max(2, Math.min(BAR_MAX_W, band - BAR_GAP));
  const ticks = [0, top / 2, top];

  // The tallest bar carries a label; the rest are left to the axis and the
  // tooltip. A value over every column is noise nobody reads.
  const peak = hasData
    ? buckets.reduce((best, b, i) => (b.revenue > buckets[best].revenue ? i : best), 0)
    : -1;

  // Every label would collide well before a month fits, so they thin out to
  // whatever the width allows and always keep the first and last.
  const step = Math.max(1, Math.ceil(buckets.length / Math.max(2, Math.floor(plotW / 44))));

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        padding: 16,
        gap: 14,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10 }}>
        <View style={{ gap: 2, flex: 1, minWidth: 0 }}>
          {/* Sans, not the brand serif: a figure is read, not admired. */}
          <T size={24} weight={700}>
            {headline.value}
          </T>
          <T size={11.5} color={colors.textFaint} numberOfLines={1}>
            {headline.detail}
          </T>
        </View>
        {selected ? (
          <Pressable onPress={() => setPicked(null)} testID="chart-clear" style={{ padding: 4 }}>
            <T size={11.5} weight={700} color={colors.gold}>
              ×
            </T>
          </Pressable>
        ) : null}
      </View>

      {hasData ? (
        <View onLayout={onLayout} style={{ height: PLOT_H + AXIS_H }}>
          {width > 0 ? (
            <Svg width={width} height={PLOT_H + AXIS_H}>
              {/* Gridlines first, so the data sits on top of the chrome. */}
              {ticks.map((v) => {
                const y = PLOT_H - (top > 0 ? (v / top) * PLOT_H : 0);
                return (
                  <G key={`t${v}`}>
                    <Line
                      x1={AXIS_W}
                      y1={y}
                      x2={width}
                      y2={y}
                      stroke={colors.line}
                      strokeWidth={1}
                    />
                    <SvgText
                      x={AXIS_W - 8}
                      y={y + 3.5}
                      textAnchor="end"
                      fontSize={9.5}
                      fill={colors.textFaint}
                    >
                      {compact(v)}
                    </SvgText>
                  </G>
                );
              })}

              {buckets.map((b, i) => {
                const x = AXIS_W + i * band + (band - barW) / 2;
                // Nothing sold is drawn as nothing. The baseline says zero.
                if (b.revenue <= 0) return null;
                const h = Math.max(2, (b.revenue / top) * PLOT_H);
                const active = picked === i;
                return (
                  <Path
                    key={b.from}
                    d={columnPath(x, PLOT_H - h, barW, h)}
                    fill={active ? colors.gold : colors.goldDim}
                  />
                );
              })}

              {/* The x-axis rule sits above the labels, closing the plot. */}
              <Line
                x1={AXIS_W}
                y1={PLOT_H}
                x2={width}
                y2={PLOT_H}
                stroke={colors.line}
                strokeWidth={1}
              />

              {buckets.map((b, i) =>
                i % step === 0 || i === buckets.length - 1 ? (
                  <SvgText
                    key={`x${b.from}`}
                    x={AXIS_W + i * band + band / 2}
                    y={PLOT_H + 13}
                    textAnchor="middle"
                    fontSize={9.5}
                    fill={colors.textFaint}
                  >
                    {b.label}
                  </SvgText>
                ) : null,
              )}

              {peak >= 0 && picked == null ? (
                <SvgText
                  x={AXIS_W + peak * band + band / 2}
                  y={PLOT_H - (buckets[peak].revenue / top) * PLOT_H - 6}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight="700"
                  fill={colors.textDim}
                >
                  {compact(buckets[peak].revenue)}
                </SvgText>
              ) : null}
            </Svg>
          ) : null}

          {/* Touch targets are laid over the plot rather than being the marks
              themselves: a one-day spike is a 6px column, and nobody hits that
              with a finger. Each covers its whole band, full height. */}
          <View style={{ position: 'absolute', left: AXIS_W, right: 0, top: 0, height: PLOT_H, flexDirection: 'row' }}>
            {buckets.map((b, i) => (
              <Pressable
                key={`hit${b.from}`}
                testID={`chart-bar-${i}`}
                onPress={() => setPicked(picked === i ? null : i)}
                style={{ flex: 1 }}
              />
            ))}
          </View>
        </View>
      ) : (
        <View style={{ paddingVertical: 34, alignItems: 'center' }}>
          <T size={12.5} color={colors.textFaint}>
            {emptyLabel}
          </T>
        </View>
      )}
    </View>
  );
}

/**
 * A column with a 4px rounded cap and square feet, so it reads as growing out
 * of the baseline rather than floating above it.
 */
function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  return [
    `M${x},${y + h}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + w - r},${y}`,
    `Q${x + w},${y} ${x + w},${y + r}`,
    `L${x + w},${y + h}`,
    'Z',
  ].join(' ');
}

/**
 * The top of the axis: a round number comfortably above the tallest bar.
 *
 * Two things at once. The ceiling is always twice a "nice" step, so the middle
 * gridline is a number a reader can do arithmetic with (0 / 12 / 24, never
 * 0 / 11.3 / 22.6). And it leaves ~12% headroom, so the tallest column never
 * reaches the top edge — which is what gives its label somewhere to sit that is
 * not on top of the bar itself.
 */
function niceCeiling(n: number): number {
  if (n <= 0) return 1;
  const wanted = n / 0.88;
  const half = wanted / 2;
  const mag = 10 ** Math.floor(Math.log10(half));
  const step =
    [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]
      .map((m) => m * mag)
      .find((c) => c >= half) ?? half;
  return step * 2;
}

/** Axis figures stay short: 20,918 becomes 20.9k, 900 stays 900. */
function compact(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
  }
  return String(Math.round(n));
}

function bucketLabel(b: Bucket): string {
  const d = new Date(b.from);
  const span = b.to - b.from;
  // An hour-wide bucket names the hour; a day-wide one names the date.
  if (span <= 60 * 60 * 1000 + 1) {
    const h = d.getHours();
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:00 ${h < 12 ? 'AM' : 'PM'}`;
  }
  return `${String(d.getDate()).padStart(2, '0')}/${d.getMonth() + 1}`;
}
